import { forwardRef, useState } from "react";
import {
  AlignItems,
  Banner,
  BannerStatus,
  BannerVariant,
  Box,
  Field,
  FormFieldStatus,
  Hint,
  Label,
  Option,
  P,
  Select,
  Space,
  BannerLeftAccessoryType,
  PlainLink,
  HoverEffect,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import { ChangeHandler } from "react-hook-form";
import { useLingui } from "@lingui/react";

import { SignUpEvents } from "../consts";

import { useSegment } from "src/technical-integrations/segment/useSegment";
import { CountryCodeSupported, countryCodeToName } from "src/common/country";
import { FieldLevelError } from "src/common/api";
import { useSupportedCountries } from "src/common/hooks/useSupportedCountries";

interface GeoDropdownProps {
  apiError?: FieldLevelError;
  userNavigatorLocale: CountryCodeSupported;
  referralCode: string | null;
  hasFsbMemberDiscount?: boolean;
  onChange?: ChangeHandler;
}

export const GeoDropdown = forwardRef<HTMLSelectElement, GeoDropdownProps>(
  (
    { apiError, userNavigatorLocale, hasFsbMemberDiscount, onChange, ...props },
    ref
  ) => {
    const [geoValue, setGeoValue] = useState(String(userNavigatorLocale));
    const { sendEvent } = useSegment();
    const { i18n } = useLingui();

    const DEFAULT_BUSINESS_LOCATION =
      countryCodeToName(i18n)[userNavigatorLocale];

    const { supportedCountries } = useSupportedCountries();

    const fsbNotSupported = !!hasFsbMemberDiscount && geoValue !== "GB";

    const countryOptions = supportedCountries.map(
      (code) =>
        code !== userNavigatorLocale && (
          <Option key={code} value={code} data-testid={code}>
            {countryCodeToName(i18n)[code]}
          </Option>
        )
    );

    return (
      <Field data-testid="geo">
        <Box layout="flex" alignItems={AlignItems.Center}>
          <Label htmlFor="geo">
            <Trans id="sign-up.form.organisation-geo.label">
              Where is your organisation based?
            </Trans>
          </Label>
        </Box>
        <Select
          id="geo"
          data-testid="select_geo"
          ref={ref}
          aria-label="Choose a country"
          defaultValue="Choose a country"
          onChange={(e) => {
            setGeoValue(e.currentTarget.value);
            sendEvent(SignUpEvents.BusinessLocationChanged, {
              business_location: e.currentTarget.value,
            });
            onChange?.(e);
          }}
          {...props}
          required
        >
          {[
            <Option
              key=""
              value={userNavigatorLocale}
              defaultValue={userNavigatorLocale}
            >
              {DEFAULT_BUSINESS_LOCATION}
            </Option>,
            ...countryOptions,
            <Option key="other" value="other">
              Other
            </Option>,
          ]}
        </Select>
        {apiError && (
          <Hint status={FormFieldStatus.Danger}>{apiError.message}</Hint>
        )}
        {geoValue === "other" && (
          <Hint status={FormFieldStatus.Danger}>
            <Trans id="sign-up.form.unsupported-geo-warning-1">
              <P>
                Unfortunately we are only able to serve the countries currently
                included in the list above.
              </P>
            </Trans>

            <Trans id="sign-up.form.unsupported-geo-warning-2">
              <P>
                If yours isn&apos;t listed, please{" "}
                <PlainLink
                  href="mailto:help@gocardless.com"
                  textDecoration="underline"
                  effect={HoverEffect.TextDecoration}
                >
                  get in touch
                </PlainLink>{" "}
                to be notified when GoCardless is available in your country.
              </P>
            </Trans>
          </Hint>
        )}
        {fsbNotSupported && (
          <>
            <Space v={0.5} />
            <Banner
              variant={BannerVariant.Light}
              leftAccessory={{
                type: BannerLeftAccessoryType.Status,
                status: BannerStatus.Warning,
              }}
            >
              <Trans>
                Please note: the 25% discount for FSB members is only available
                for organisations based in the UK
              </Trans>
            </Banner>
          </>
        )}
      </Field>
    );
  }
);
